import {faCheckCircle} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import type {SyncResultSuccessT} from '@freckle/educator-entities/ts/common/models/roster-sync'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Container} from '@freckle/educator-materials/ts/components/container'
import {ExpandableContainer} from '@freckle/educator-materials/ts/components/expandable-container'
import {H2} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import Warning from '@freckle/school/ts/rostering/components/common/warning'
import {
  header,
  checkmarkIcon,
  listGroup,
  buttonWrapper,
  resultListItem,
  resultWrapper,
  mainLabel,
  dataPointStyle
} from './confirm-roster-sync.module.scss'

type Props = {
  results: Array<SyncResultSuccessT>
  onFinish: () => void
}

export default function ConfirmRosterSync({results, onFinish}: Props) {
  const {t} = useSafeTranslation()

  const expandableList = (
    <>
      {results.map((result, i: number) => {
        const {schoolName} = result
        return <p key={i}>{schoolName}</p>
      })}
    </>
  )

  const resultData = getResultData(results)

  return (
    <Container>
      <div className={header}>
        <FontAwesomeIcon className={checkmarkIcon} icon={faCheckCircle} />
        <H2>{t('CONFIRM_ROSTER_SYNC_H2_HEADING')}</H2>
      </div>

      <ul className={listGroup}>
        <li>{t('CONFIRM_ROSTER_SYNC_LIST_GROUP_HEADING')}</li>
        <li>
          <ExpandableContainer
            expandableComponent={expandableList}
            theme="basic"
            chevronPosition="left"
            iconExpand="down"
            iconCollapse="right"
          >
            {(_open: boolean) => (
              <span>
                {t('CONFIRM_ROSTER_SYNC_LIST_GROUP_RESULT_COUNT', {
                  numResults: results.length
                })}
              </span>
            )}
          </ExpandableContainer>
        </li>

        <ResultItem
          label={t('CONFIRM_ROSTER_SYNC_RESULT_ITEM_STUDENTS')}
          data={resultData.students}
        />
        <ResultItem
          label={t('CONFIRM_ROSTER_SYNC_RESULT_ITEM_TEACHERS')}
          data={resultData.teachers}
        />
        <ResultItem
          label={t('CONFIRM_ROSTER_SYNC_RESULT_ITEM_COURSES')}
          data={resultData.courses}
        />
        <ResultItem
          label={t('CONFIRM_ROSTER_SYNC_RESULT_ITEM_ENROLLMENTS')}
          data={resultData.courseMemberships}
        />
      </ul>

      <Warning text={t('CONFIRM_ROSTER_SYNC_WARNING_FOR_STUDENTS')} />
      <Warning text={t('CONFIRM_ROSTER_SYNC_WARNING_FOR_UNTOUCHED_SCHOOLS')} />

      <div className={buttonWrapper}>
        <Button dataTest="rostering-sync-and-finish" onClick={onFinish}>
          {t('CONFIRM_ROSTER_SYNC_BUTTON_FINISH')}
        </Button>
      </div>
    </Container>
  )
}

type ResultDataT = {
  students: ResultCountsT
  teachers: ResultCountsT
  courses: ResultCountsT
  courseMemberships: ResultCountsT
}

type ResultCountsT = {
  added: number
  deleted: number
  unchanged: number
  updated: number
}

function getResultData(results: Array<SyncResultSuccessT>): ResultDataT {
  const initialCounts = {
    added: 0,
    deleted: 0,
    unchanged: 0,
    updated: 0
  }

  const initialAcc = {
    students: initialCounts,
    teachers: initialCounts,
    courses: initialCounts,
    courseMemberships: initialCounts
  }

  const resultData = results.reduce((acc: ResultDataT, result: SyncResultSuccessT) => {
    const {unchangedCounts, updatedCounts, deletedCounts, addedCounts} = result
    const {students, teachers, courses, courseMemberships} = acc

    const studentData = {
      added: students.added + addedCounts.studentCount,
      deleted: students.deleted + deletedCounts.studentCount,
      unchanged: students.unchanged + unchangedCounts.studentCount,
      updated: students.updated + updatedCounts.studentCount
    }

    const teacherData = {
      added: teachers.added + addedCounts.teacherCount,
      deleted: teachers.deleted + deletedCounts.teacherCount,
      unchanged: teachers.unchanged + unchangedCounts.teacherCount,
      updated: teachers.updated + updatedCounts.teacherCount
    }

    const courseData = {
      added: courses.added + addedCounts.courseCount,
      deleted: courses.deleted + deletedCounts.courseCount,
      unchanged: courses.unchanged + unchangedCounts.courseCount,
      updated: courses.updated + updatedCounts.courseCount
    }

    const courseMembershipData = {
      added: courseMemberships.added + addedCounts.courseMembershipCount,
      deleted: courseMemberships.deleted + deletedCounts.courseMembershipCount,
      unchanged: courseMemberships.unchanged + unchangedCounts.courseMembershipCount,
      updated: courseMemberships.updated + updatedCounts.courseMembershipCount
    }

    return {
      students: studentData,
      teachers: teacherData,
      courses: courseData,
      courseMemberships: courseMembershipData
    }
  }, initialAcc)

  return resultData
}

type ResultItemProps = {
  label: string
  data: ResultCountsT
}

function ResultItem(props: ResultItemProps) {
  const {label, data} = props
  const {t} = useSafeTranslation()

  return (
    <li className={resultListItem}>
      <div className={resultWrapper}>
        <div className={mainLabel}>
          <span>
            <strong>{label}</strong>
          </span>
        </div>
        <span className={dataPointStyle}>
          <strong>{t('CONFIRM_ROSTER_SYNC_RESULT_ITEM_STAT_ADDED')}</strong> {data.added}
        </span>
        <span className={dataPointStyle}>
          <strong>{t('CONFIRM_ROSTER_SYNC_RESULT_ITEM_STAT_UPDATED')}</strong> {data.updated}
        </span>
        <span className={dataPointStyle}>
          <strong>{t('CONFIRM_ROSTER_SYNC_RESULT_ITEM_STAT_UNCHANGED')}</strong> {data.unchanged}
        </span>
        <span className={dataPointStyle}>
          <strong>{t('CONFIRM_ROSTER_SYNC_RESULT_ITEM_STAT_DELETED')}</strong> {data.deleted}
        </span>
      </div>
    </li>
  )
}
